import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabase';
import { LanguageProvider } from './contexts/LanguageContext';
import { AdFreeSubscriberProvider } from './contexts/AdFreeSubscriberContext';
import ErrorBoundary from './components/ErrorBoundary';
import CloudflareStatusBanner from './components/CloudflareStatusBanner';
import RouteTransitionLoader from './components/RouteTransitionLoader';
import { initializeNotifications } from './utils/notificationUtils';
import { initializeAudioOnUserInteraction } from './utils/soundEffects';
import { syncLocalStorageToDatabase } from './utils/syncProgress';
import { handleOAuthCallback } from './utils/authUtils';
import { initializeAdMob } from './utils/adMobService';
import BannerAdManager from './components/BannerAdManager';
import { isAndroid } from './utils/platformUtils';
import './App.css';

// Import screens
import OnboardingScreen from './screens/OnboardingScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import HomeScreen from './screens/HomeScreen';
import LevelsScreen from './screens/LevelsScreen';
import ChaptersScreen from './screens/ChaptersScreen';
import LessonsScreen from './screens/LessonsScreen';
import LessonScreen from './screens/LessonScreen';
import QuizScreen from './screens/QuizScreen';
import QuizListScreen from './screens/QuizListScreen';
import ProfileScreen from './screens/ProfileScreen';
import SettingsScreen from './screens/SettingsScreen';
import PrivacySecurityScreen from './screens/PrivacySecurityScreen';
import HelpSupportScreen from './screens/HelpSupportScreen';
import LeaderboardScreen from './screens/LeaderboardScreen';
import WordsScreen from './screens/WordsScreen';
import WordsDetailScreen from './screens/WordsDetailScreen';
import GrammarScreen from './screens/GrammarScreen';
import GrammarDetailScreen from './screens/GrammarDetailScreen';
import WordMeaningScreen from './screens/WordMeaningScreen';
import OppositeWordsScreen from './screens/OppositeWordsScreen';
import ConversationsScreen from './screens/ConversationsScreen';
import ConversationDetailScreen from './screens/ConversationDetailScreen';
import WritingScreen from './screens/WritingScreen';
import EbookScreen from './screens/EbookScreen';
import EbookReaderScreen from './screens/EbookReaderScreen';
import PaidBookReaderScreen from './screens/PaidBookReaderScreen';

interface User {
  id: string;
  email: string;
  full_name?: string;
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(() => {
    // Initialize with guest user if in guest mode
    const isGuest = typeof window !== 'undefined' && localStorage.getItem('isGuest') === 'true';
    if (isGuest) {
      return {
        id: 'guest',
        email: 'guest@shikhunitalian.com',
        full_name: 'Guest User'
      };
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [currentScreen, setCurrentScreen] = useState<string>('onboarding');
  useEffect(() => {
    // Initialize audio on first user interaction (required for Android/iOS)
    initializeAudioOnUserInteraction();
    // Initialize AdMob SDK on native mobile (no-op on web)
    initializeAdMob();
    
    // Handle OAuth callback from deep link (Android/iOS)
    let appUrlListener: any = null;
    
    const setupOAuthCallbackHandler = async () => {
      // Check if running in Capacitor
      if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform()) {
        const Capacitor = (window as any).Capacitor;
        const { App } = Capacitor.Plugins || {};
        
        if (App) {
          // Check initial URL if app was launched from deep link
          try {
            const appState = await App.getState();
            console.log('📱 App state:', appState);
            
            // Get initial URL (if app was launched from deep link)
            const initialUrl = await App.getLaunchUrl();
            if (initialUrl && initialUrl.url) {
              console.log('🔗 App launched from deep link:', initialUrl.url);
              console.log('📱 Processing initial URL callback...');
              
              const success = await handleOAuthCallback(initialUrl.url);
              console.log('📱 Callback handler result:', success);
              
              if (success) {
                // Wait a bit for session to be established
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                console.log('📱 Session check result:', {
                  hasSession: !!session,
                  hasUser: !!session?.user,
                  userEmail: session?.user?.email,
                  error: sessionError?.message,
                });
                
                if (session?.user) {
                  console.log('✅ User logged in from deep link:', session.user.email);
                  setUser(session.user as User);
                  setCurrentScreen('home');
                  setLoading(false); // Ensure loading is cleared
                  syncLocalStorageToDatabase();
                  // The auth state change listener will also fire, but we set user here too
                  return; // Early return if we handled the callback
                } else {
                  console.error('❌ Session not found after callback handling');
                  setLoading(false);
                }
              } else {
                console.error('❌ Callback handler returned false');
              }
            }
          } catch (error) {
            console.warn('⚠️ Could not get initial URL:', error);
          }
          
          // Listen for app URL open (when app is already running)
          if (App.addListener) {
            appUrlListener = await App.addListener('appUrlOpen', async (data: { url: string }) => {
              console.log('🔗 Deep link received (app running):', data.url);
              
              const success = await handleOAuthCallback(data.url);
              console.log('🔗 Callback handler result (app running):', success);
              
              if (success) {
                // Wait a bit for session to be established
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Check session after callback
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                console.log('🔗 Session check (app running):', {
                  hasSession: !!session,
                  hasUser: !!session?.user,
                  userEmail: session?.user?.email,
                  error: sessionError?.message,
                });
                
                if (session?.user) {
                  console.log('✅ User logged in (app running):', session.user.email);
                  setUser(session.user as User);
                  setCurrentScreen('home');
                  setLoading(false); // Ensure loading is cleared
                  // Sync localStorage data to database on login
                  syncLocalStorageToDatabase();
                  // The auth state change listener will also fire, but we set user here too
                } else {
                  console.error('❌ Session not found (app running)');
                  setLoading(false);
                }
              } else {
                console.error('❌ Callback handler returned false (app running)');
              }
            });
            
            console.log('✅ Deep link listener registered for Android/iOS');
          }
        } else {
          console.warn('⚠️ Capacitor App plugin not available');
        }
      }
      
      // Also check for OAuth callback in URL hash (web and mobile web)
      // Process this BEFORE checking existing session
      const hash = window.location.hash;
      if (hash && (hash.includes('access_token') || hash.includes('type=recovery'))) {
        console.log('✅ OAuth callback in URL hash detected (web)');
        console.log('🔗 Current pathname:', window.location.pathname);
        console.log('🔗 Hash:', hash.substring(0, 100) + '...');
        
        // Process the OAuth callback immediately
        try {
          const success = await handleOAuthCallback(window.location.href);
          if (success) {
            // Wait a moment for session to be fully established
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Verify session was established
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
              console.log('✅ Session confirmed after OAuth callback:', session.user.email);
              setUser(session.user as User);
              setCurrentScreen('home');
              syncLocalStorageToDatabase();
              // Clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname);
              setLoading(false);
              return; // Early return - don't check existing session
            } else {
              console.error('❌ Session not found after OAuth callback processing');
            }
          } else {
            console.error('❌ OAuth callback processing returned false');
          }
        } catch (hashError) {
          console.error('❌ Error processing OAuth hash:', hashError);
        }
        
        // Clear the hash even if processing failed
        window.history.replaceState(null, '', window.location.pathname);
      }
    };
    
    // Setup OAuth callback handler
    setupOAuthCallbackHandler();
    
    // Check for existing session or guest mode
    const checkSession = async () => {
      try {
        // First check for guest mode (synchronous check)
        const isGuest = typeof window !== 'undefined' && localStorage.getItem('isGuest') === 'true';
        if (isGuest) {
          // Allow guest access - create a guest user object immediately
          const guestUser: User = {
            id: 'guest',
            email: 'guest@shikhunitalian.com',
            full_name: 'Guest User'
          };
          setUser(guestUser);
          setCurrentScreen('home');
          setLoading(false);
          return; // Don't check session if guest
        }

        // Check for authenticated session (cached check first for faster load)
        const sessionCacheKey = 'auth_session_check';
        const cachedSession = sessionStorage.getItem(sessionCacheKey);
        
        if (cachedSession === 'true') {
          // Quick check - session likely still valid
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            setUser(session.user as User);
            setCurrentScreen('home');
            setLoading(false);
            // Sync in background (non-blocking)
            setTimeout(() => syncLocalStorageToDatabase(), 100);
            return;
          }
        }

        // Full session check
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user as User);
          setCurrentScreen('home');
          sessionStorage.setItem(sessionCacheKey, 'true');
          // Sync in background (non-blocking)
          setTimeout(() => syncLocalStorageToDatabase(), 100);
        } else {
          setUser(null);
          sessionStorage.removeItem(sessionCacheKey);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        // Always set loading to false on error
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    // Add a safety timeout to ensure loading never stays true indefinitely
    const loadingTimeout = setTimeout(() => {
      console.warn('Loading timeout reached, forcing loading to false');
      setLoading(false);
    }, 8000); // 8 second maximum loading time

    checkSession();

    // Initialize notifications
    initializeNotifications();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state changed:', event, session?.user?.email);
        if (session?.user) {
          setUser(session.user as User);
          setCurrentScreen('home');
          // Clear guest mode when user logs in
          localStorage.removeItem('isGuest');
          // Sync progress when user logs in
          syncLocalStorageToDatabase();
        } else {
          // Check if user is in guest mode
          const isGuest = localStorage.getItem('isGuest') === 'true';
          if (isGuest) {
            // Keep guest access
            setUser({
              id: 'guest',
              email: 'guest@shikhunitalian.com',
              full_name: 'Guest User'
            });
          setCurrentScreen('home');
        } else {
          setUser(null);
          setCurrentScreen('onboarding');
          }
        }
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
      // Remove deep link listener if it was registered
      if (appUrlListener && appUrlListener.remove) {
        appUrlListener.remove();
      }
    };
  }, []);

  const handleLogout = async () => {
    try {
      const isGuest = user?.id === 'guest';
      if (isGuest) {
        // Guest logout - just clear guest flag
        localStorage.removeItem('isGuest');
        setUser(null);
        window.location.href = '/onboarding';
      } else {
        // Regular user logout
      await supabase.auth.signOut();
      setUser(null);
      // Don't clear localStorage - keep progress for when user logs back in
      window.location.href = '/onboarding';
      }
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Error logging out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src="/logo512.png" alt="Shikhun Italian" />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AdFreeSubscriberProvider>
        <Router>
          <BannerAdManager />
          <RouteTransitionLoader>
            <div className="app">
              <CloudflareStatusBanner />
              <Routes>
            <Route path="/" element={(user || localStorage.getItem('isGuest') === 'true') ? <Navigate to="/home" replace /> : <Navigate to="/onboarding" replace />} />
            <Route path="/onboarding" element={(user || localStorage.getItem('isGuest') === 'true') ? <Navigate to="/home" replace /> : <OnboardingScreen />} />
            <Route path="/login" element={(user && user.id !== 'guest') ? <Navigate to="/home" replace /> : <LoginScreen />} />
            <Route path="/register" element={(user && user.id !== 'guest') ? <Navigate to="/home" replace /> : <RegisterScreen />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/home" replace /> : <ForgotPasswordScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/home" element={(user || localStorage.getItem('isGuest') === 'true') ? <HomeScreen user={user || { id: 'guest', email: 'guest@shikhunitalian.com', full_name: 'Guest User' }} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
            <Route path="/levels" element={(user || localStorage.getItem('isGuest') === 'true') ? <LevelsScreen /> : <Navigate to="/login" replace />} />
            <Route path="/chapters/:levelId" element={(user || localStorage.getItem('isGuest') === 'true') ? <ChaptersScreen /> : <Navigate to="/login" replace />} />
            <Route path="/chapter/:chapterId/lessons" element={(user || localStorage.getItem('isGuest') === 'true') ? <LessonsScreen /> : <Navigate to="/login" replace />} />
            <Route path="/lesson/:lessonId" element={(user || localStorage.getItem('isGuest') === 'true') ? <LessonScreen /> : <Navigate to="/login" replace />} />
            <Route path="/quiz/:lessonId" element={(user || localStorage.getItem('isGuest') === 'true') ? <QuizScreen /> : <Navigate to="/login" replace />} />
            <Route path="/quizzes" element={(user || localStorage.getItem('isGuest') === 'true') ? <QuizListScreen /> : <Navigate to="/login" replace />} />
            <Route path="/profile" element={(user || localStorage.getItem('isGuest') === 'true') ? <ProfileScreen user={user || { id: 'guest', email: 'guest@shikhunitalian.com', full_name: 'Guest User' }} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
            <Route path="/settings" element={(user || localStorage.getItem('isGuest') === 'true') ? <SettingsScreen user={user || { id: 'guest', email: 'guest@shikhunitalian.com', full_name: 'Guest User' }} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
            <Route path="/privacy-security" element={(user || localStorage.getItem('isGuest') === 'true') ? <PrivacySecurityScreen /> : <Navigate to="/login" replace />} />
            <Route path="/help-support" element={(user || localStorage.getItem('isGuest') === 'true') ? <HelpSupportScreen /> : <Navigate to="/login" replace />} />
            <Route path="/leaderboard" element={(user || localStorage.getItem('isGuest') === 'true') ? <LeaderboardScreen /> : <Navigate to="/login" replace />} />
            <Route path="/words" element={(user || localStorage.getItem('isGuest') === 'true') ? <WordsScreen /> : <Navigate to="/login" replace />} />
            <Route path="/words/:type" element={(user || localStorage.getItem('isGuest') === 'true') ? <WordsDetailScreen /> : <Navigate to="/login" replace />} />
            <Route path="/grammar" element={(user || localStorage.getItem('isGuest') === 'true') ? <GrammarScreen /> : <Navigate to="/login" replace />} />
            <Route path="/grammar/:type" element={(user || localStorage.getItem('isGuest') === 'true') ? <GrammarDetailScreen /> : <Navigate to="/login" replace />} />
            <Route path="/word-meaning" element={(user || localStorage.getItem('isGuest') === 'true') ? <WordMeaningScreen /> : <Navigate to="/login" replace />} />
            <Route path="/opposite-words" element={(user || localStorage.getItem('isGuest') === 'true') ? <OppositeWordsScreen /> : <Navigate to="/login" replace />} />
            <Route path="/conversations" element={(user || localStorage.getItem('isGuest') === 'true') ? <ConversationsScreen /> : <Navigate to="/login" replace />} />
            <Route path="/conversations/:conversationId" element={(user || localStorage.getItem('isGuest') === 'true') ? <ConversationDetailScreen /> : <Navigate to="/login" replace />} />
            <Route path="/writing" element={(user || localStorage.getItem('isGuest') === 'true') ? <WritingScreen /> : <Navigate to="/login" replace />} />
            <Route path="/ebooks" element={(user || localStorage.getItem('isGuest') === 'true') ? <EbookScreen /> : <Navigate to="/login" replace />} />
            <Route path="/ebooks/:id" element={(user || localStorage.getItem('isGuest') === 'true') ? <EbookReaderScreen /> : <Navigate to="/login" replace />} />
            {/* Paid books are keyed by their Play product id (a slug like 'book_a0_a1'), not the
                numeric ebooks id, so this route is kept off /ebooks/:id entirely. The guard below
                only gates the screen; entitlement is decided server-side by get-book-url. */}
            {/* Android native only. The same React bundle is served as a
                website, where FLAG_SECURE is a no-op — so a real entitlement
                would render the PDF with screenshots, printing and a
                downloadable signed URL all available in DevTools. The
                entitlement gate would hold; every anti-export control would
                not. */}
            <Route path="/paid-book/:productId" element={isAndroid() && (user || localStorage.getItem('isGuest') === 'true') ? <PaidBookReaderScreen /> : <Navigate to="/ebooks" replace />} />
              </Routes>
            </div>
          </RouteTransitionLoader>
        </Router>
        </AdFreeSubscriberProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

export default App;